export interface ValidationResponse {
    errors: Object;
    message: string;
}

export function instanceOfValidationResponse(object: any): object is ValidationResponse {
    return typeof object === "object" && object && "errors" in object && "message" in object;
}

export function parseGenericObjectToFormData<T extends object>(
    object: T,
    form?: FormData,
    namespace?: string
): FormData {
    const formData = form || new FormData();
    for (const property in object) {
        const isPropertyExist = property in object;

        if (!isPropertyExist) {
            continue;
        }

        const contextProperty = object[property];
        const formKey = namespace ? `${namespace}[${property}]` : property;

        if (typeof contextProperty === "object" && !(contextProperty instanceof File)) {
            parseGenericObjectToFormData<any>(contextProperty, formData, formKey);
        } else {
            formData.append(formKey, contextProperty instanceof File ? contextProperty : String(contextProperty));
        }
    }
    return formData;
}

export const roundAccurately = (number: number | null, precision: number = 2) => {
    if (number === null) {
        return null;
    }

    if (number > Number.MAX_SAFE_INTEGER) {
        return 0;
    }

    let roundedNum = parseFloat((number * 10 ** precision + 0.1 ** (precision + 2)).toFixed(0));
    return parseFloat((roundedNum / 10 ** precision).toFixed(precision));
};

export const formatNumber = (
    number: number | null,
    opts?: {
        precision?: number;
        currency?: boolean;
        suffix?: string;
    }
) => {
    const options = {
        precision: 2,
        currency: false,
        suffix: "",
        ...opts,
    };

    if (options.currency && options.suffix == "") {
        options.suffix = " zł";
    }

    // Odpowiednie zaokrąglenie.
    let val = roundAccurately(number ?? 0, options.precision).toString();

    // Dodanie separatora tysięcy przed kropką.
    // Rozdzielenie części całkowitej od ułamkowej.
    const [integer, decimal] = val.split(".");
    const integerWithSeparator = integer.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    // Połączenie części całkowitej z ułamkową.
    val = integerWithSeparator + (decimal ? `.${decimal}` : "");

    // Dodanie tylu zer na końcu, ile wynosi precyzja
    const decimalLength = decimal ? decimal.length : 0;
    if (decimalLength < options.precision) {
        // Jeśli nie ma części ułamkowej, to dodajemy na końcu kropkę.
        if (!decimal) {
            val += ".";
        }

        val += "0".repeat(options.precision - decimalLength);
    }

    // Zamienienie kropki na przecinki.
    val = val.replace(".", ",");

    // Dodanie sufiksu.
    if (options.suffix != "") {
        val += options.suffix;
    }

    return val;
};

/**
 * Funkcja pomocnicza do generowanie obiektu FormData.
 *
 * Dba o to, aby dane zostały odpowiednio sformatowane.
 *
 * @param data
 * @param opt
 */
export function generateFormData(
    data: Record<string, any>,
    opt: {
        fillable?: string[];
        list_fields?: string[];
        bool_fields?: string[];
    }
) {
    const options = {
        fillable: [],
        list_fields: [],
        bool_fields: [],
        ...opt,
    };

    const fd = new FormData();

    for (const field in data) {
        if (!options.fillable.includes(field)) {
            continue;
        }

        const v = data[field];
        if (v === null || v === "") {
            continue;
        }
        if (options.list_fields.includes(field)) {
            // Kluczem wartości z listy może być "id" lub "key".
            fd.set(field, v["id"] ?? v["key"]);
        } else if (options.bool_fields.includes(field)) {
            fd.set(field, v ? "1" : "0");
        } else {
            fd.set(field, v instanceof File ? v : v.toString());
        }
    }

    return fd;
}
